import React from 'react';

import EventIcon from '@mui/icons-material/EventOutlined';
import HotelIcon from '@mui/icons-material/HotelOutlined';
import TourIcon from '@mui/icons-material/Tour';
import { Box, Card, CardContent, Divider, Stack, Typography } from '@mui/material';

import { useGetOffer } from '~/hooks/apis/offers/useGetOffer';

import { Bundle, HotelBundleComponent } from '~/types/bundles/bundle';

import TourOfferName from './TourOfferName';
import useBundleTourSpan from './useBundleTourSpan';

interface Props {
  bundle: Bundle;
}

function pluralise(count: number, noun: string) {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

function Row({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Box color="text.secondary" pt={0.25}>
        {icon}
      </Box>
      <Box minWidth={0}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="body2" component="div">
          {children}
        </Typography>
      </Box>
    </Stack>
  );
}

function HotelLine({ hotel }: { hotel: HotelBundleComponent }) {
  const { data: offer } = useGetOffer(hotel.offer_id);
  const day = hotel.day_offset ?? 0;
  return (
    <div>
      {offer?.name ?? 'Flash hotel'} · {pluralise(hotel.nights, 'night')} from day {day + 1}
    </div>
  );
}

/** What the bundle is made of so far, and the one thing to do next. */
export default function BundleOverview({ bundle }: Props) {
  const { tour, tourDays, startDay } = useBundleTourSpan(bundle);
  const hotels = bundle.components.filter(
    (component): component is HotelBundleComponent => component.product_type === 'flashAccommodation',
  );
  const departures = bundle.departure_dates;

  const nextStep = !tour
    ? 'Add the base tour.'
    : hotels.length === 0
      ? 'Pair it with a flash hotel.'
      : departures.length === 0
        ? 'Choose the departures to sell.'
        : 'Ready for approval.';

  return (
    <Card variant="outlined" sx={{ position: { md: 'sticky' }, top: { md: 88 } }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary">
          Overview
        </Typography>
        <Stack spacing={2} mt={1}>
          <Row icon={<TourIcon fontSize="small" />} label="Tour">
            {tour ? (
              <>
                <TourOfferName tourOfferId={tour.tour_offer_id} />
                {tourDays !== undefined && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Days {startDay + 1}–{startDay + tourDays}
                  </Typography>
                )}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Not added yet
              </Typography>
            )}
          </Row>

          <Row icon={<HotelIcon fontSize="small" />} label="Hotel">
            {hotels.length > 0 ? (
              hotels.map((hotel) => <HotelLine key={hotel.id} hotel={hotel} />)
            ) : (
              <Typography variant="body2" color="text.secondary">
                Not added yet
              </Typography>
            )}
          </Row>

          <Row icon={<EventIcon fontSize="small" />} label="Departures">
            {departures.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                None selected
              </Typography>
            ) : (
              <span>{pluralise(departures.length, 'departure')}</span>
            )}
          </Row>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Typography variant="caption" color="text.secondary">
          Next
        </Typography>
        <Typography variant="body2" fontWeight="medium">
          {nextStep}
        </Typography>
      </CardContent>
    </Card>
  );
}
